import FableTooltip from "./common/FableTooltip";
import BarChartIcon from "@mui/icons-material/BarChart";
import React, { ReactNode } from "react";
import {
  Box,
  ButtonBase,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import GroupsIcon from "@mui/icons-material/Groups";
import HomeIcon from "@mui/icons-material/Home";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import NotificationsIcon from "@mui/icons-material/Notifications";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import ShareIcon from "@mui/icons-material/Share";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import { NAVIGATION_CONTROL_HEIGHT } from "./common/buttonSizing";
import { WEB_RELEASE_FLAGS } from "../config/releaseFlags";
import {
  DASHBOARD_NAV_COLLAPSED_WIDTH,
  DASHBOARD_NAV_EXPANDED_WIDTH,
} from "../contexts/DashboardShellContext";

const APP_BACKGROUND = "#101219";
const APP_BORDER = "rgba(255,255,255,0.12)";
const ACCENT_GOLD = "#D8A363";
const SELECTED_SURFACE = "#1B1E28";
const SELECTED_SURFACE_HOVER = "#20232E";
const TEXT_SECONDARY = "#B9BAC5";
const fableIcon = "/fable-butterfly-gold.png?v=butterfly-20260916";
const coinImage = "/coin.png";

type NavigationItem = {
  label: string;
  path: string;
  icon: ReactNode;
};

type NavigationSection = {
  label?: string;
  items: NavigationItem[];
};

const navigationSections: NavigationSection[] = [
  {
    items: [
      { label: "My Stories", path: "/my-stories", icon: <AutoStoriesIcon /> },
      { label: "Create", path: "/create", icon: <AddCircleOutlineIcon /> },
      { label: "Account", path: "/settings", icon: <PersonOutlineIcon /> },
    ],
  },
  {
    label: "Billing",
    items: [
      {
        label: "Subscription",
        path: "/manage-subscription",
        icon: <img src="/crown.png" alt="" style={{ width: 26, height: 26 }} />,
      },
      {
        label: "Buy Tokens",
        path: "/buy-tokens",
        icon: <img src={coinImage} alt="" style={{ width: 26, height: 26 }} />,
      },
      { label: "Usage", path: "/usage", icon: <BarChartIcon /> },
    ],
  },
  {
    label: "Library",
    items: [
      { label: "Your Characters", path: "/characters", icon: <GroupsIcon /> },
      { label: "Your Voices", path: "/voices", icon: <RecordVoiceOverIcon /> },
    ],
  },
  {
    label: "Social Media",
    items: [
      {
        label: "Connected Accounts",
        path: "/social-accounts",
        icon: <ShareIcon />,
      },
      ...(WEB_RELEASE_FLAGS.socialAnalyticsUiEnabled ? [{
        label: "Analytics",
        path: "/social-accounts/analytics",
        icon: <InsightsOutlinedIcon />,
      }] : []),
    ],
  },
  {
    label: "Books & Orders",
    items: [
      {
        label: "Create & Order Book",
        path: "/book-builder",
        icon: <MenuBookIcon />,
      },
      { label: "My Orders", path: "/my-orders", icon: <LocalShippingIcon /> },
    ],
  },
  {
    label: "Preferences",
    items: [
      {
        label: "Notifications",
        path: "/notifications",
        icon: <NotificationsIcon />,
      },
    ],
  },
  {
    label: "Help",
    items: [
      { label: "Support", path: "/support", icon: <ContactSupportIcon /> },
    ],
  },
];

const itemIsActive = (pathname: string, path: string) => {
  if (path === "/my-stories") {
    return (
      pathname === path ||
      pathname.startsWith("/story-preview/") ||
      pathname.startsWith("/edit-story/") ||
      pathname === "/share" ||
      pathname === "/download" ||
      pathname.startsWith("/create-next-chapter/")
    );
  }
  if (path === "/create") {
    return (
      pathname === path ||
      pathname === "/create/gambit" ||
      pathname === "/create/detail" ||
      pathname === "/create/confirm"
    );
  }
  if (path === "/usage") return pathname === path || pathname.startsWith("/usage/");
  if (path === "/social-accounts") return pathname === path;
  return pathname === path || pathname.startsWith(`${path}/`);
};

type NavigationContentProps = {
  expanded: boolean;
  mobile?: boolean;
  onClose?: () => void;
  onToggleExpanded?: (expanded: boolean) => void;
  account?: {
    tokens: number;
    email: string | null;
    tier?: string | null;
    isPremium: boolean;
    isPlatinum: boolean;
  };
  onSignOut?: () => void;
};

const DashboardNavigation: React.FC<NavigationContentProps> = ({
  expanded,
  mobile = false,
  onClose,
  onToggleExpanded,
  account,
  onSignOut,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = account;
  const tierLabel = user?.tier === 'ultra' ? 'Ultra' : user?.isPlatinum
    ? "Pro"
    : user?.isPremium
      ? "Premium"
      : "Free";

  const handleNavigate = (path: string) => {
    onClose?.();
    navigate(
      path === "/create" && location.pathname === "/create/gambit"
        ? location.pathname
        : path,
    );
  };

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: APP_BACKGROUND,
      }}
    >
      <Stack
        direction={expanded ? "row" : "column"}
        alignItems="center"
        justifyContent={expanded ? "space-between" : "flex-start"}
        spacing={expanded ? 1 : 1.1}
        sx={{ p: expanded ? 2.5 : 1.5, pb: expanded ? 2 : 1.5 }}
      >
        <FableTooltip title="Fable home" placement="left">
          <ButtonBase
            aria-label="Fable home"
            onClick={() => handleNavigate("/")}
            sx={{
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: expanded ? "flex-start" : "center",
              gap: 1,
              width: expanded ? "auto" : NAVIGATION_CONTROL_HEIGHT,
              height: NAVIGATION_CONTROL_HEIGHT,
              flexShrink: 0,
              borderRadius: 999,
            }}
          >
            <img
              src={fableIcon}
              alt="Fable"
              style={{
                width: expanded ? 42 : 48,
                height: expanded ? 42 : 48,
                borderRadius: "50%",
                flexShrink: 0,
                objectFit: "contain",
                objectPosition: "center",
              }}
            />
            {expanded && (
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: 24,
                  lineHeight: 1,
                  fontWeight: 900,
                }}
              >
                fable
              </Typography>
            )}
          </ButtonBase>
        </FableTooltip>

        {mobile ? (
          <IconButton
            aria-label="Close navigation"
            onClick={onClose}
            sx={{
              color: "#fff",
              border: "none",
              bgcolor: "transparent",
              boxShadow: "none",
              "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        ) : (
          <FableTooltip
            title={expanded ? "Collapse navigation" : "Expand navigation"}
            placement="left"
          >
            <IconButton
              aria-label={
                expanded ? "Collapse navigation" : "Expand navigation"
              }
              onClick={() => onToggleExpanded?.(!expanded)}
              sx={{
                width: NAVIGATION_CONTROL_HEIGHT,
                height: NAVIGATION_CONTROL_HEIGHT,
                color: "#fff",
                bgcolor: "rgba(255,255,255,0.04)",
                border: `1px solid ${APP_BORDER}`,
                "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
              }}
            >
              {expanded ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </FableTooltip>
        )}
      </Stack>

      {user && !expanded && !mobile && (
        <Stack spacing={0.75} sx={{ px: 1, mb: 1.25, alignItems: "center" }}>
          <FableTooltip title={`${user.tokens || 0} tokens`} placement="left">
            <ButtonBase
              aria-label={`View token options. ${user.tokens || 0} tokens available`}
              onClick={() => handleNavigate("/buy-tokens")}
              sx={{
                width: NAVIGATION_CONTROL_HEIGHT,
                height: NAVIGATION_CONTROL_HEIGHT,
                flexShrink: 0,
                borderRadius: "50%",
                bgcolor: "rgba(216,163,99,0.1)",
                border: "1px solid rgba(216,163,99,0.28)",
                "&:hover": {
                  bgcolor: "rgba(216,163,99,0.18)",
                  borderColor: "rgba(216,163,99,0.48)",
                },
              }}
            >
              <img src={coinImage} alt="" style={{ width: 32, height: 32 }} />
            </ButtonBase>
          </FableTooltip>

          <FableTooltip title={tierLabel} placement="left">
            <ButtonBase
              aria-label={`View subscription options. Current membership: ${tierLabel}`}
              onClick={() => handleNavigate("/manage-subscription")}
              sx={{
                width: NAVIGATION_CONTROL_HEIGHT,
                height: NAVIGATION_CONTROL_HEIGHT,
                flexShrink: 0,
                borderRadius: "50%",
                bgcolor: "rgba(132,82,220,0.12)",
                border: "1px solid rgba(177,130,255,0.28)",
                "&:hover": {
                  bgcolor: "rgba(132,82,220,0.21)",
                  borderColor: "rgba(177,130,255,0.5)",
                },
              }}
            >
              <img src="/crown.png" alt="" style={{ width: 30, height: 30 }} />
            </ButtonBase>
          </FableTooltip>
        </Stack>
      )}

      {user && expanded && (
        <Stack spacing={0.75} sx={{ mx: 1.25, mb: 1.25 }}>
          <ButtonBase
            aria-label="View token options"
            onClick={() => handleNavigate("/buy-tokens")}
            sx={{
              px: 1.5,
              py: 0.5,
              minHeight: NAVIGATION_CONTROL_HEIGHT,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 1,
              borderRadius: 3,
              textAlign: "left",
              bgcolor: "rgba(216,163,99,0.1)",
              border: "1px solid rgba(216,163,99,0.28)",
              "&:hover": {
                bgcolor: "rgba(216,163,99,0.16)",
                borderColor: "rgba(216,163,99,0.42)",
              },
            }}
          >
            <img src={coinImage} alt="" style={{ width: 28, height: 28 }} />
            <Box sx={{ minWidth: 0, flex: 1, textAlign: "left" }}>
              <Typography
                sx={{ color: ACCENT_GOLD, fontWeight: 900, lineHeight: 1.1 }}
              >
                {user.tokens || 0} tokens
              </Typography>
              <Typography
                sx={{ color: TEXT_SECONDARY, fontSize: 12, mt: 0.25 }}
              >
                Token balance
              </Typography>
            </Box>
          </ButtonBase>

          <ButtonBase
            aria-label="View subscription options"
            onClick={() => handleNavigate("/manage-subscription")}
            sx={{
              px: 1.5,
              py: 0.5,
              minHeight: NAVIGATION_CONTROL_HEIGHT,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 1,
              borderRadius: 3,
              textAlign: "left",
              bgcolor: "rgba(132,82,220,0.12)",
              border: "1px solid rgba(177,130,255,0.28)",
              "&:hover": {
                bgcolor: "rgba(132,82,220,0.19)",
                borderColor: "rgba(177,130,255,0.44)",
              },
            }}
          >
            <img src="/crown.png" alt="" style={{ width: 28, height: 28 }} />
            <Box sx={{ minWidth: 0, flex: 1, textAlign: "left" }}>
              <Typography
                sx={{ color: "#fff", fontWeight: 900, lineHeight: 1.1 }}
              >
                {tierLabel}
              </Typography>
              <Typography
                noWrap
                sx={{ color: TEXT_SECONDARY, fontSize: 12, mt: 0.25 }}
              >
                {user.email}
              </Typography>
            </Box>
          </ButtonBase>
        </Stack>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          px: expanded ? 1.25 : 1,
          pb: 1,
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(255,255,255,0.2) transparent",
        }}
      >
        {navigationSections
          .filter((section) => user || section.label !== "Billing")
          .map((section, sectionIndex) => (
            <Box
              key={section.label || "primary"}
              sx={{
                mt: sectionIndex === 0 ? 0 : 1.4,
                pt: sectionIndex === 0 ? 0 : 1.4,
                borderTop:
                  sectionIndex === 0 ? "none" : `1px solid ${APP_BORDER}`,
              }}
            >
              {expanded && section.label && (
                <Typography
                  sx={{
                    pl: 2.4,
                    pr: 1.5,
                    mb: 0.6,
                    color: "rgba(255,255,255,0.5)",
                    fontSize: 11,
                    fontWeight: 900,
                    letterSpacing: "0.08em",
                    textAlign: "left",
                    textTransform: "uppercase",
                  }}
                >
                  {section.label}
                </Typography>
              )}
              <List disablePadding>
                {section.items.map((item) => {
                  const active = itemIsActive(location.pathname, item.path);
                  const itemButton = (
                    <ListItemButton
                      key={item.path}
                      aria-label={item.label}
                      aria-current={active ? "page" : undefined}
                      onClick={() => handleNavigate(item.path)}
                      sx={{
                        minHeight: NAVIGATION_CONTROL_HEIGHT,
                        width: expanded ? "100%" : NAVIGATION_CONTROL_HEIGHT,
                        height: expanded ? "auto" : NAVIGATION_CONTROL_HEIGHT,
                        mx: expanded ? 0 : "auto",
                        mb: 0.45,
                        px: expanded ? 1.5 : 0,
                        justifyContent: expanded ? "flex-start" : "center",
                        borderRadius: expanded ? 3 : "50%",
                        color: active ? "#fff" : "rgba(255,255,255,0.78)",
                        bgcolor: active ? SELECTED_SURFACE : "transparent",
                        border: active
                          ? "2px solid #D8A363"
                          : "2px solid transparent",
                        "&:hover": {
                          bgcolor: active
                            ? SELECTED_SURFACE_HOVER
                            : "rgba(255,255,255,0.07)",
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: expanded ? 38 : 0,
                          color: active ? ACCENT_GOLD : "inherit",
                          justifyContent: "center",
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {expanded && (
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontSize: 14,
                            lineHeight: 1.2,
                            fontWeight: active ? 850 : 650,
                          }}
                        />
                      )}
                    </ListItemButton>
                  );

                  return expanded ? (
                    <React.Fragment key={item.path}>
                      {itemButton}
                    </React.Fragment>
                  ) : (
                    <FableTooltip
                      key={item.path}
                      title={item.label}
                      placement="left"
                    >
                      {itemButton}
                    </FableTooltip>
                  );
                })}
              </List>
            </Box>
          ))}
      </Box>

      <Box sx={{ p: expanded ? 1.5 : 1, borderTop: `1px solid ${APP_BORDER}` }}>
        <FableTooltip title={expanded ? "" : "Back to Home"} placement="left">
          <ListItemButton
            aria-label="Back to Home"
            onClick={() => handleNavigate("/")}
            sx={{
              minHeight: NAVIGATION_CONTROL_HEIGHT,
              width: expanded ? "100%" : NAVIGATION_CONTROL_HEIGHT,
              height: expanded ? "auto" : NAVIGATION_CONTROL_HEIGHT,
              mx: expanded ? 0 : "auto",
              px: expanded ? 1.5 : 0,
              justifyContent: expanded ? "flex-start" : "center",
              color: "rgba(255,255,255,0.72)",
              borderRadius: expanded ? 3 : "50%",
            }}
          >
            <ListItemIcon
              sx={{ minWidth: expanded ? 38 : 0, color: "inherit" }}
            >
              <HomeIcon />
            </ListItemIcon>
            {expanded && <ListItemText primary="Back to Home" />}
          </ListItemButton>
        </FableTooltip>
        {onSignOut && (
          <FableTooltip title={expanded ? "" : "Sign Out"} placement="left">
            <ListItemButton
              aria-label="Sign Out"
              onClick={() => {
                onClose?.();
                onSignOut();
              }}
              sx={{
                minHeight: NAVIGATION_CONTROL_HEIGHT,
                width: expanded ? "100%" : NAVIGATION_CONTROL_HEIGHT,
                height: expanded ? "auto" : NAVIGATION_CONTROL_HEIGHT,
                mx: expanded ? 0 : "auto",
                px: expanded ? 1.5 : 0,
                justifyContent: expanded ? "flex-start" : "center",
                color: "rgba(255,255,255,0.72)",
                borderRadius: expanded ? 3 : "50%",
                "&:hover": { color: "#ff7777", bgcolor: "rgba(239,68,68,0.1)" },
              }}
            >
              <ListItemIcon
                sx={{ minWidth: expanded ? 38 : 0, color: "inherit" }}
              >
                <LogoutIcon />
              </ListItemIcon>
              {expanded && <ListItemText primary="Sign Out" />}
            </ListItemButton>
          </FableTooltip>
        )}
      </Box>
    </Box>
  );
};

export function DashboardNavigationRail({
  expanded,
  children,
}: React.PropsWithChildren<{ expanded: boolean }>) {
  return (
    <Box
      component="aside"
      aria-label="Dashboard navigation"
      sx={{
        position: "fixed",
        zIndex: 1400,
        top: 0,
        right: 0,
        bottom: 0,
        width: expanded
          ? DASHBOARD_NAV_EXPANDED_WIDTH
          : DASHBOARD_NAV_COLLAPSED_WIDTH,
        bgcolor: APP_BACKGROUND,
        borderLeft: `1px solid ${APP_BORDER}`,
        boxShadow: expanded ? "-18px 0 42px rgba(0,0,0,0.18)" : "none",
        transition: "width 0.22s ease, box-shadow 0.22s ease",
      }}
    >
      {children}
    </Box>
  );
}

export function DashboardNavigationDrawer({
  open,
  onClose,
  children,
}: React.PropsWithChildren<{ open: boolean; onClose: () => void }>) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          id: "fable-navigation",
          role: "dialog",
          "aria-label": "Navigation",
        },
      }}
      sx={{
        zIndex: 1400,
        "& .MuiDrawer-paper": {
          width: "min(320px, calc(100vw - 32px))",
          top: 0,
          height: "100%",
          bgcolor: APP_BACKGROUND,
          borderLeft: `1px solid ${APP_BORDER}`,
        },
      }}
    >
      {children}
    </Drawer>
  );
}

export default DashboardNavigation;
